/**
 * Centralized SEO / GEO constants and JSON-LD schema builders.
 *
 * The goal is a single source of truth for the canonical site URL, default
 * social-share image, and the entity graph (Organization, WebSite) that AI
 * search engines use to disambiguate Quilty as a real, citable entity.
 *
 * Pages should compose these node builders into a single `@graph` block via
 * `buildGraph(...)` and render it through `JsonLd.svelte`. Consolidating into
 * one `@graph` lets crawlers parse the whole entity network in a single pass.
 */
import type { Article, Dataset, Offer, Organization, Product, WebSite, Thing, WithContext } from 'schema-dts';

/** Canonical production origin. Used for absolute URLs in structured data. */
export const SITE_URL = 'https://quilty.app';

/** Default Open Graph / Twitter share image. */
export const DEFAULT_OG_IMAGE = `${SITE_URL}/images/og-home.jpg`;

/** Twitter / X handle used across MetaTags. */
export const TWITTER_HANDLE = '@quiltyapp';

/** Stable @id anchors so nodes can reference one another within the graph. */
export const ORG_ID = `${SITE_URL}/#organization`;
export const WEBSITE_ID = `${SITE_URL}/#website`;

/** Build an absolute URL from a site-relative path. */
export function absoluteUrl(path = '/'): string {
  if (path === '/') return SITE_URL;
  return `${SITE_URL}${path.startsWith('/') ? path : `/${path}`}`;
}

/**
 * Organization node. `sameAs` only lists profiles we can verify to avoid
 * sending crawlers to dead links (which weakens entity trust).
 */
export function organizationSchema(): Organization {
  return {
    '@type': 'Organization',
    '@id': ORG_ID,
    name: 'Quilty',
    alternateName: 'Quilty.app',
    url: SITE_URL,
    logo: `${SITE_URL}/favicon.png`,
    description:
      'Quilty is an AI-powered screenplay analysis and entertainment market intelligence platform. It delivers professional script coverage and a transparent 0-100 Quilty Score, and tracks the film and TV business — weekend box office, production greenlights, sales, and talent moves — for writers, producers, financiers, and executives.',
    slogan: "Entertainment's intelligence platform.",
    knowsAbout: [
      'Screenplay analysis',
      'Script coverage',
      'Film market intelligence',
      'Box office trends',
      'Film and television development',
      'Talent and casting analysis',
      'Comparable films analysis',
    ],
    sameAs: ['https://twitter.com/quiltyapp'],
    contactPoint: {
      '@type': 'ContactPoint',
      email: 'demo@quilty.app',
      contactType: 'Sales',
    },
  };
}

/** WebSite node, linked to the Organization as publisher. */
export function websiteSchema(): WebSite {
  return {
    '@type': 'WebSite',
    '@id': WEBSITE_ID,
    url: SITE_URL,
    name: 'Quilty',
    description:
      'AI-powered screenplay analysis, script coverage, and film market intelligence.',
    publisher: { '@id': ORG_ID },
  };
}

/** A single FAQ question/answer pair. */
export interface FaqItem {
  q: string;
  a: string;
}

/** FAQPage node built from question/answer pairs for GEO answer extraction. */
export function faqPageSchema(items: readonly FaqItem[]) {
  return {
    '@type': 'FAQPage' as const,
    mainEntity: items.map((item) => ({
      '@type': 'Question' as const,
      name: item.q,
      acceptedAnswer: {
        '@type': 'Answer' as const,
        text: item.a,
      },
    })),
  };
}

/** A single item in a CollectionPage / ItemList. */
export interface ListItemInput {
  url: string;
  name: string;
  description?: string;
}

/** ItemList node for hub/collection pages. */
export function itemListSchema(items: readonly ListItemInput[]) {
  return {
    '@type': 'ItemList' as const,
    itemListElement: items.map((item, index) => ({
      '@type': 'ListItem' as const,
      position: index + 1,
      url: absoluteUrl(item.url),
      name: item.name,
      ...(item.description ? { description: item.description } : {}),
    })),
  };
}

/**
 * A schema-dts-compatible `@graph` container. schema-dts does not export a
 * convenient context+graph type, so we describe it locally.
 */
export type GraphSchema = {
  '@context': 'https://schema.org';
  '@graph': Thing[];
};

/** Compose multiple schema nodes into one `@graph` block. */
export function buildGraph(...nodes: Thing[]): GraphSchema {
  return {
    '@context': 'https://schema.org',
    '@graph': nodes,
  };
}

/** Convenience: the shared Organization + WebSite nodes most pages include. */
export function baseEntityNodes(): Thing[] {
  return [organizationSchema() as Thing, websiteSchema() as Thing];
}

/** Inputs for blog Article JSON-LD. */
export interface ArticleInput {
  title: string;
  description: string;
  path: string;
  datePublished: string;
  dateModified?: string;
  author?: string;
  image?: string;
  tags?: readonly string[];
}

/** Article node for blog posts and long-form explainers. */
export function articleSchema(input: ArticleInput): Article {
  const url = absoluteUrl(input.path);
  return {
    '@type': 'Article',
    '@id': `${url}#article`,
    headline: input.title,
    description: input.description,
    url,
    datePublished: input.datePublished,
    dateModified: input.dateModified ?? input.datePublished,
    author: {
      '@type': 'Organization',
      name: input.author ?? 'Quilty',
      url: SITE_URL,
    },
    publisher: { '@id': ORG_ID },
    image: input.image ?? DEFAULT_OG_IMAGE,
    mainEntityOfPage: url,
    isPartOf: { '@id': WEBSITE_ID },
    ...(input.tags?.length ? { keywords: input.tags.join(', ') } : {}),
  };
}

/** Inputs for empirical data-report Dataset JSON-LD. */
export interface DatasetInput {
  name: string;
  description: string;
  path: string;
  dateModified: string;
  keywords?: string;
  variableMeasured?: readonly string[];
}

/** Dataset node for original research posts (GEO citation target). */
export function datasetSchema(input: DatasetInput): Dataset {
  const url = absoluteUrl(input.path);
  return {
    '@type': 'Dataset',
    name: input.name,
    description: input.description,
    url,
    creator: { '@id': ORG_ID },
    publisher: { '@id': ORG_ID },
    dateModified: input.dateModified,
    license: `${SITE_URL}/terms`,
    ...(input.keywords ? { keywords: input.keywords } : {}),
    ...(input.variableMeasured?.length
      ? {
          variableMeasured: input.variableMeasured.map((name) => ({
            '@type': 'PropertyValue' as const,
            name,
          })),
        }
      : {}),
  };
}

/** Format catalog/list cents as a schema.org Offer price string (e.g. 5499 → "54.99"). */
export function priceStringFromCents(cents: number): string {
  return (cents / 100).toFixed(2);
}

/** One-time purchase Offer (analysis SKUs). */
export function oneTimeOffer(opts: {
  name: string;
  /** Decimal dollars matching the visible price (e.g. 49.99). */
  price: number;
  url?: string;
}): Offer {
  return {
    '@type': 'Offer',
    name: opts.name,
    price: opts.price.toFixed(2),
    priceCurrency: 'USD',
    availability: 'https://schema.org/InStock',
    url: absoluteUrl(opts.url ?? '/pricing'),
  };
}

/**
 * Monthly subscription Offer with UnitPriceSpecification (Discovery Network).
 * `monthCents` must match the billing catalog (QUI-161).
 */
export function monthlySubscriptionOffer(opts: {
  name: string;
  monthCents: number;
  url?: string;
}): Offer {
  const price = priceStringFromCents(opts.monthCents);
  const url = absoluteUrl(opts.url ?? '/discovery/membership');
  return {
    '@type': 'Offer',
    name: opts.name,
    url,
    price,
    priceCurrency: 'USD',
    availability: 'https://schema.org/InStock',
    priceSpecification: {
      '@type': 'UnitPriceSpecification',
      price,
      priceCurrency: 'USD',
      billingDuration: 'P1M',
      billingIncrement: 1,
      unitText: 'month',
    },
  };
}

/** Product node wrapping one or more Offers. */
export function productSchema(opts: {
  name: string;
  description: string;
  url: string;
  offers: Offer | Offer[];
}): Product {
  return {
    '@type': 'Product',
    name: opts.name,
    description: opts.description,
    brand: { '@type': 'Brand', name: 'Quilty' },
    url: absoluteUrl(opts.url),
    offers: opts.offers,
  };
}

export type { Thing, WithContext, Offer, Product };
